import React from 'react';
import classnames from 'classnames/bind';
import { Link } from 'react-router-dom';

import {
  DEPLOYMENT_ACTIVE_STATES,
  DEPLOYMENT_ERROR_STATES,
  DEPLOYMENT_INACTIVE_STATES,
} from 'types/deployment';

import { useAPI } from 'hooks/api/useAPI';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useDeployments } from 'deployments/hooks/useDeployments';
import { useHasAccess } from 'hooks/useHasAccess';

import { Button } from 'components/Button/Button';
import { Card } from 'components/Card/Card';
import { DeployRoute, NavRoute } from 'application/types/routes';
import { Heading } from 'components/Heading/Heading';
import { Tooltip } from 'components/Tooltip/Tooltip';

import { DashboardNumber } from 'application/views/Overview/Number/Number';
import styles from './Overview.module.scss';

const c = classnames.bind(styles);

export function OverviewDeployments() {
  const { applicationID } = useAPI();
  const { isSimplifiedUxPlan } = useCurrentPlan();
  const [hasAccess] = useHasAccess();
  const { data: active } = useDeployments({
    params: {
      limit: 1,
      states: [...DEPLOYMENT_ACTIVE_STATES],
    },
    keepPreviousData: true,
  });
  const { data: stopped } = useDeployments({
    params: {
      limit: 1,
      states: [...DEPLOYMENT_INACTIVE_STATES],
    },
    keepPreviousData: true,
  });
  const { data: error } = useDeployments({
    params: {
      limit: 1,
      states: [...DEPLOYMENT_ERROR_STATES],
    },
    keepPreviousData: true,
  });

  const activeCount = active ? active.total_elements : 0;
  const stoppedCount = stopped ? stopped.total_elements : 0;
  const errorCount = error ? error.total_elements : 0;

  return (
    <Card className={c('item')}>
      <Heading level="4" className={c('item-name')} asChild>
        <h3>
          <Link to="deploy/deployments" className={c('item-link')}>
            Workflow deployments
          </Link>
        </h3>
      </Heading>

      <dl className={c('item-deployment-numbers')}>
        <DashboardNumber
          number={activeCount}
          status="running"
          to="deploy/deployments"
          state={{
            filters: {
              state: 'running',
            },
          }}
        />
        <DashboardNumber
          number={errorCount}
          status="offline"
          to="deploy/deployments"
          state={{
            filters: {
              state: 'error',
            },
          }}
        />
        <DashboardNumber
          number={stoppedCount}
          status="stopped"
          to="deploy/deployments"
          state={{
            filters: {
              state: 'stopped',
            },
          }}
        />
      </dl>

      <div className={c('item-graph')} aria-hidden="true">
        {activeCount > 0 && (
          <Tooltip side="top" content={`${activeCount} deployments running`}>
            <div
              className={c('item-graph-line')}
              style={{ '--count': activeCount } as React.CSSProperties}
            >
              <div className={c('item-graph-line-fill', 'theme success')}>
                <Link
                  className={c('item-graph-link')}
                  to="deploy/deployments"
                  state={{
                    filters: {
                      state: 'running',
                    },
                  }}
                />
              </div>
            </div>
          </Tooltip>
        )}
        {errorCount > 0 && (
          <Tooltip
            side="top"
            content={`${errorCount} deployments in error state`}
          >
            <div
              className={c('item-graph-line')}
              style={{ '--count': errorCount } as React.CSSProperties}
            >
              <div className={c('item-graph-line-fill', 'theme danger')}>
                <Link
                  className={c('item-graph-link')}
                  to="deploy/deployments"
                  state={{
                    filters: {
                      state: 'error',
                    },
                  }}
                />
              </div>
            </div>
          </Tooltip>
        )}
        {stoppedCount > 0 && (
          <Tooltip side="top" content={`${stoppedCount} deployments stopped`}>
            <div
              className={c('item-graph-line')}
              style={{ '--count': stoppedCount } as React.CSSProperties}
            >
              <div className={c('item-graph-line-fill', 'theme warning')}>
                <Link
                  className={c('item-graph-link')}
                  to="deploy/deployments"
                  state={{
                    filters: {
                      state: 'stopped',
                    },
                  }}
                />
              </div>
            </div>
          </Tooltip>
        )}
      </div>

      {hasAccess('deploy_edit') && !isSimplifiedUxPlan && (
        <Button
          to={`/applications/${applicationID}/${NavRoute.DEPLOY}/${DeployRoute.RAPID_DEPLOY}`}
          variant="primary"
          size="small"
        >
          Deploy workflows
        </Button>
      )}
    </Card>
  );
}
